import { getOnedata, UpdateOneData } from '../controllers/APIController';

/**
 * Simple Workflow Service
 * Handles basic workflow operations for tasks
 */
class WorkflowService {
  /**
   * Get workflow template by ID
   */
  static async getWorkflow(workflowId) {
    try {
      const response = await getOnedata('workflows', workflowId);
      return response?.data?.attributes || null;
    } catch (error) {
      console.error('Error getting workflow:', error);
      return null;
    }
  }

  /**
   * Get task by ID
   */
  static async getTask(taskId) {
    try {
      const response = await getOnedata('tasks', taskId);
      return response?.data?.attributes || null;
    } catch (error) {
      console.error('Error getting task:', error);
      return null;
    }
  }

  /**
   * Update task with new data
   */
  static async updateTask(taskId, updateData) {
    try {
      await UpdateOneData('tasks', taskId, updateData);
      return true;
    } catch (error) {
      console.error('Error updating task:', error);
      return false;
    }
  }

  /**
   * Get workflow status for a task
   */
  static async getTaskWorkflowStatus(taskId) {
    try {
      const task = await this.getTask(taskId);
      if (!task || !task.workflowId) {
        return { hasWorkflow: false, message: 'No workflow assigned' };
      }

      const workflow = await this.getWorkflow(task.workflowId);
      if (!workflow) {
        return { hasWorkflow: false, message: 'Workflow not found' };
      }

      const steps = workflow.steps || [];
      const currentStep = steps.find(step => step.id === task.currentStepId) || steps[0];

      if (!currentStep) {
        return { hasWorkflow: false, message: 'No workflow steps found' };
      }

      return {
        hasWorkflow: true,
        workflowId: task.workflowId,
        workflowName: workflow.Name,
        currentStep: currentStep,
        totalSteps: steps.length,
        currentStepNumber: steps.findIndex(step => step.id === task.currentStepId) + 1,
        availableActions: currentStep.actions || [],
        progress: Math.round(((steps.findIndex(step => step.id === task.currentStepId) + 1) / steps.length) * 100)
      };
    } catch (error) {
      console.error('Error getting workflow status:', error);
      return { hasWorkflow: false, message: 'Error loading workflow status' };
    }
  }

  /**
   * Process workflow action
   */
  static async processWorkflowAction(taskId, actionName, userId, comments = '') {
    try {
      const task = await this.getTask(taskId);
      if (!task || !task.workflowId) {
        throw new Error('Task not found or no workflow assigned');
      }

      const workflow = await this.getWorkflow(task.workflowId);
      if (!workflow) {
        throw new Error('Workflow not found');
      }

      const steps = workflow.steps || [];
      const currentStep = steps.find(step => step.id === task.currentStepId);
      if (!currentStep) {
        throw new Error('Current step not found');
      }

      const action = currentStep.actions?.find(a => a.buttonName === actionName);
      if (!action) {
        throw new Error('Action not found');
      }

      // Update task
      const updateData = {
        currentStepId: action.nextStepId || null,
        Status: action.status || 'in_progress',
        LastUpdated: new Date().toISOString()
      };

      // Add to history if it exists
      if (task.history) {
        updateData.history = [
          ...task.history,
          {
            stepId: currentStep.id,
            stepName: currentStep.name,
            action: actionName,
            status: action.status,
            timestamp: new Date().toISOString(),
            performedBy: userId,
            comments: comments
          }
        ];
      }

      const success = await this.updateTask(taskId, updateData);
      if (!success) {
        throw new Error('Failed to update task');
      }

      return {
        success: true,
        taskId: taskId,
        action: actionName,
        newStatus: action.status,
        nextStepId: action.nextStepId
      };
    } catch (error) {
      console.error('Error processing workflow action:', error);
      throw error;
    }
  }
}

export default WorkflowService;

